// src/services/taskService.js (updated sharing methods)
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  or,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, COLLECTIONS } from './firebase';
import { auth } from './firebase';

export class AssessmentService {
  // Get current user ID
  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  // Check if user is authenticated
  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  // Assessment Tasks
  static async createAssessmentTask(task) {
    const userId = AssessmentService.ensureAuthenticated();

    const docRef = await addDoc(collection(db, COLLECTIONS.ASSESSMENT_TASKS), {
      ...task,
      createdBy: userId,
      sharedWith: [],
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return docRef.id;
  }

  static async duplicateAssessmentTask(originalTaskId) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Get the original task
      const originalTask = await AssessmentService.getAssessmentTask(originalTaskId);
      
      if (!originalTask) {
        throw new Error('Original task not found');
      }

      // Check if user can access the original task
      if (originalTask.createdBy !== userId && !originalTask.sharedWith.includes(userId)) {
        throw new Error('Permission denied: You cannot duplicate this task');
      }

      // Extract the fields we want to copy, excluding id and timestamps
      const {
        id,           // Remove original id
        createdAt,    // Remove original createdAt
        updatedAt,    // Remove original updatedAt
        ...taskDataToCopy
      } = originalTask;

      // Prepare the duplicated task data
      const duplicatedTaskData = {
        // Copy all fields except the ones we excluded above
        ...taskDataToCopy,
        
        // Set new ownership and metadata
        createdBy: userId,
        sharedWith: [],
        
        // Modify name to indicate it's a copy
        name: `${originalTask.name} (Copy)`,
        
        // Reset dates
        dueDate: null,
        assignedDate: null,
        
        // Generate new IDs for questions and subquestions
        questions: originalTask.questions?.map(question => {
          const {
            id: questionId,  // Remove original question id
            ...questionData
          } = question;
          
          return {
            ...questionData,
            id: `dup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            subquestions: question.subquestions?.map(subquestion => {
              const {
                id: subquestionId,  // Remove original subquestion id
                ...subquestionData
              } = subquestion;
              
              return {
                ...subquestionData,
                id: `dup-sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
              };
            }) || []
          };
        }) || [],
        
        // Set new timestamps
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Create the new task
      const docRef = await addDoc(collection(db, COLLECTIONS.ASSESSMENT_TASKS), duplicatedTaskData);
      
      // Return the complete task data with the new ID
      return {
        id: docRef.id,
        ...duplicatedTaskData
      };
    } catch (error) {
      console.error('Error duplicating task:', error);
      throw error;
    }
  }

  static async getAssessmentTasks() {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Query for tasks owned by user or shared with user
      const q = query(
        collection(db, COLLECTIONS.ASSESSMENT_TASKS),
        or(
          where('createdBy', '==', userId),
          where('sharedWith', 'array-contains', userId)
        ),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to Date objects
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw new Error('Failed to fetch tasks');
    }
  }

  static async getAssessmentTask(taskId) {
    AssessmentService.ensureAuthenticated();

    try {
      const docRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      } else {
        throw new Error('Task not found');
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      throw new Error('Failed to fetch task');
    }
  }

  static async updateAssessmentTask(id, updates) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, id);
      
      // Verify user owns the task before updating
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only edit tasks you created');
      }

      await updateDoc(taskRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

  static async deleteAssessmentTask(id) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, id);
      
      // Verify user owns the task before deleting
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only delete tasks you created');
      }

      // Delete associated files from storage
      if (taskData.taskFiles && taskData.taskFiles.length > 0) {
        for (const file of taskData.taskFiles) {
          try {
            const fileRef = ref(storage, `assessment-tasks/${id}/${file.name}`);
            await deleteObject(fileRef);
          } catch (error) {
            console.warn('Error deleting file:', error);
          }
        }
      }

      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // Enhanced Task Sharing Methods
  static async shareTask(taskId, userIds) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      // Verify user owns the task
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only share tasks you created');
      }

      // Filter out user IDs that are already shared
      const currentSharedWith = taskData.sharedWith || [];
      const newUserIds = userIds.filter(id => !currentSharedWith.includes(id));

      if (newUserIds.length > 0) {
        await updateDoc(taskRef, {
          sharedWith: arrayUnion(...newUserIds),
          updatedAt: new Date()
        });
      }

      return { success: true, message: `Task shared with ${newUserIds.length} new user(s)` };
    } catch (error) {
      console.error('Error sharing task:', error);
      throw error;
    }
  }

  static async unshareTask(taskId, userIds) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      // Verify user owns the task
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only unshare tasks you created');
      }

      // Remove users from sharedWith array
      await updateDoc(taskRef, {
        sharedWith: arrayRemove(...userIds),
        updatedAt: new Date()
      });

      return { success: true, message: `Task unshared from ${userIds.length} user(s)` };
    } catch (error) {
      console.error('Error unsharing task:', error);
      throw error;
    }
  }

  // Get shared users for a task
  static async getTaskSharedUsers(taskId) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskDoc = await getDoc(doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId));
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }

      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only view sharing info for tasks you created');
      }

      return taskData.sharedWith || [];
    } catch (error) {
      console.error('Error getting shared users:', error);
      throw error;
    }
  }

  // Bulk share task with multiple users
  static async bulkShareTask(taskId, userIds, shareType = 'read') {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      // Verify user owns the task
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only share tasks you created');
      }

      // Add sharing metadata
      const sharingInfo = userIds.map(id => ({
        userId: id,
        shareType: shareType,
        sharedAt: new Date()
      }));

      await updateDoc(taskRef, {
        sharedWith: arrayUnion(...userIds),
        sharingInfo: arrayUnion(...sharingInfo),
        updatedAt: new Date()
      });

      return { success: true, message: `Task shared with ${userIds.length} user(s)` };
    } catch (error) {
      console.error('Error bulk sharing task:', error);
      throw error;
    }
  }

  // Media Upload
  static async uploadMedia(file, taskId, fileName = null) {
    AssessmentService.ensureAuthenticated();

    try {
      const actualFileName = fileName || file.name;
      const storageRef = ref(storage, `assessment-tasks/${taskId}/${actualFileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Failed to upload file');
    }
  }

  static async deleteMedia(taskId, fileName) {
    AssessmentService.ensureAuthenticated();

    try {
      const fileRef = ref(storage, `assessment-tasks/${taskId}/${fileName}`);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw new Error('Failed to delete file');
    }
  }

  // Task Submissions
  static async createTaskSubmission(submission) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.TASK_SUBMISSIONS), {
        ...submission,
        createdBy: userId,
        createdAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw new Error('Failed to create submission');
    }
  }

  static async getTaskSubmissions(taskId = null) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      let q;
      if (taskId) {
        // Check if user can access this task
        const canAccess = await this.canAccessTask(taskId);
        if (!canAccess) {
          throw new Error('Permission denied: You cannot access submissions for this task');
        }

        q = query(
          collection(db, COLLECTIONS.TASK_SUBMISSIONS),
          where('taskId', '==', taskId),
          orderBy('date', 'desc')
        );
      } else {
        // Get all submissions for tasks user can access
        const accessibleTaskIds = await AssessmentService.getAccessibleTaskIds();
        if (accessibleTaskIds.length === 0) {
          return [];
        }

        q = query(
          collection(db, COLLECTIONS.TASK_SUBMISSIONS),
          where('taskId', 'in', accessibleTaskIds.slice(0, 10)), // Firestore 'in' limit
          orderBy('date', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      }));
    } catch (error) {
      console.error('Error fetching submissions:', error);
      throw new Error('Failed to fetch submissions');
    }
  }

  // Learner Marks
  static async createLearnerMark(mark) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Verify user can access the task
      const canAccess = await this.canAccessTask(mark.taskId);
      if (!canAccess) {
        throw new Error('Permission denied: You cannot create marks for this task');
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.LEARNER_MARKS), {
        ...mark,
        createdBy: userId,
        createdAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating learner mark:', error);
      throw new Error('Failed to create learner mark');
    }
  }

  static async getLearnerMarks(taskId = null, submissionId = null) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      let q = query(collection(db, COLLECTIONS.LEARNER_MARKS));
      
      const conditions = [];

      if (taskId) {
        // Check if user can access this task
        const canAccess = await this.canAccessTask(taskId);
        if (!canAccess) {
          throw new Error('Permission denied: You cannot access marks for this task');
        }
        conditions.push(where('taskId', '==', taskId));
      } else {
        // Get marks for accessible tasks only
        const accessibleTaskIds = await AssessmentService.getAccessibleTaskIds();
        if (accessibleTaskIds.length === 0) {
          return [];
        }
        conditions.push(where('taskId', 'in', accessibleTaskIds.slice(0, 10))); // Firestore 'in' limit
      }

      if (submissionId) {
        conditions.push(where('submissionId', '==', submissionId));
      }

      // Apply all conditions
      q = query(q, ...conditions);

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      }));
    } catch (error) {
      console.error('Error fetching learner marks:', error);
      throw new Error('Failed to fetch learner marks');
    }
  }

  static async bulkCreateLearnerMarks(marks) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Verify user can access all tasks
      const taskIds = [...new Set(marks.map(mark => mark.taskId))];
      for (const taskId of taskIds) {
        const canAccess = await this.canAccessTask(taskId);
        if (!canAccess) {
          throw new Error(`Permission denied: You cannot create marks for task ${taskId}`);
        }
      }

      const promises = marks.map(mark => 
        addDoc(collection(db, COLLECTIONS.LEARNER_MARKS), {
          ...mark,
          createdBy: userId,
          createdAt: new Date()
        })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error bulk creating learner marks:', error);
      throw new Error('Failed to create learner marks');
    }
  }

  // Helper method to get accessible task IDs
  static async getAccessibleTaskIds() {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const tasks = await this.getAssessmentTasks();
      return tasks.map(task => task.id);
    } catch (error) {
      console.error('Error getting accessible task IDs:', error);
      return [];
    }
  }

  // Check if user can access task
  static async canAccessTask(taskId) {
    const userId = this.getCurrentUserId();
    if (!userId) return false;

    try {
      const taskDoc = await getDoc(doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId));
      if (!taskDoc.exists()) return false;

      const taskData = taskDoc.data();
      return taskData.createdBy === userId || taskData.sharedWith?.includes(userId);
    } catch (error) {
      console.error('Error checking task access:', error);
      return false;
    }
  }

  // Get task owner info
  static async getTaskOwner(taskId) {
    try {
      const taskDoc = await getDoc(doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId));
      if (!taskDoc.exists()) throw new Error('Task not found');

      return taskDoc.data().createdBy;
    } catch (error) {
      console.error('Error getting task owner:', error);
      throw error;
    }
  }

  // Real-time subscription methods remain the same...
  static subscribeToTasks(callback) {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const q = query(
        collection(db, COLLECTIONS.ASSESSMENT_TASKS),
        or(
          where('createdBy', '==', userId),
          where('sharedWith', 'array-contains', userId)
        ),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const tasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
        }));
        callback(tasks);
      }, (error) => {
        console.error('Error in task subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up task subscription:', error);
      throw new Error('Failed to set up real-time updates');
    }
  }

  static subscribeToTask(taskId, callback) {
    AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      return onSnapshot(taskRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          callback({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        } else {
          callback(null);
        }
      }, (error) => {
        console.error('Error in task subscription:', error);
        callback(null);
      });
    } catch (error) {
      console.error('Error setting up task subscription:', error);
      throw new Error('Failed to set up real-time updates');
    }
  }

  static async deleteTaskSubmission(submissionId) {
  const userId = AssessmentService.ensureAuthenticated();

  try {
    const submissionRef = doc(db, COLLECTIONS.TASK_SUBMISSIONS, submissionId);
    
    // Verify user owns the submission before deleting
    const submissionDoc = await getDoc(submissionRef);
    if (!submissionDoc.exists()) {
      throw new Error('Submission not found');
    }
    
    const submissionData = submissionDoc.data();
    if (submissionData.createdBy !== userId) {
      throw new Error('Permission denied: You can only delete submissions you created');
    }

    await deleteDoc(submissionRef);
  } catch (error) {
    console.error('Error deleting submission:', error);
    throw error;
  }
}
static async updateLearnerMark(markId, updates) {
  const userId = this.ensureAuthenticated();

  try {
    const markRef = doc(db, COLLECTIONS.LEARNER_MARKS, markId);
    
    // Verify user owns the mark before updating
    const markDoc = await getDoc(markRef);
    if (!markDoc.exists()) {
      throw new Error('Mark not found');
    }
    
    const markData = markDoc.data();
    if (markData.createdBy !== userId) {
      throw new Error('Permission denied: You can only edit marks you created');
    }

    await updateDoc(markRef, {
      ...updates,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating mark:', error);
    throw error;
  }
}

static async deleteLearnerMark(markId) {
  const userId = this.ensureAuthenticated();

  try {
    const markRef = doc(db, COLLECTIONS.LEARNER_MARKS, markId);
    
    // Verify user owns the mark before deleting
    const markDoc = await getDoc(markRef);
    if (!markDoc.exists()) {
      throw new Error('Mark not found');
    }
    
    const markData = markDoc.data();
    if (markData.createdBy !== userId) {
      throw new Error('Permission denied: You can only delete marks you created');
    }

    await deleteDoc(markRef);
  } catch (error) {
    console.error('Error deleting mark:', error);
    throw error;
  }
}

  // Assessment Tasks
  static async createAssessmentTask(task) {
    const userId = AssessmentService.ensureAuthenticated();

    const docRef = await addDoc(collection(db, COLLECTIONS.ASSESSMENT_TASKS), {
      ...task,
      createdBy: userId,
      sharedWith: [],
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return docRef.id;
  }

  static async getAssessmentTasks() {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Query for tasks owned by user or shared with user
      const q = query(
        collection(db, COLLECTIONS.ASSESSMENT_TASKS),
        or(
          where('createdBy', '==', userId),
          where('sharedWith', 'array-contains', userId)
        ),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to Date objects
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw new Error('Failed to fetch tasks');
    }
  }

  static async getAssessmentTask(taskId) {
    AssessmentService.ensureAuthenticated();

    try {
      const docRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      } else {
        throw new Error('Task not found');
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      throw new Error('Failed to fetch task');
    }
  }

  static async updateAssessmentTask(id, updates) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, id);
      
      // Verify user owns the task before updating
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only edit tasks you created');
      }

      await updateDoc(taskRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

  static async deleteAssessmentTask(id) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, id);
      
      // Verify user owns the task before deleting
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only delete tasks you created');
      }

      // Delete associated files from storage
      if (taskData.taskFiles && taskData.taskFiles.length > 0) {
        for (const file of taskData.taskFiles) {
          try {
            const fileRef = ref(storage, `assessment-tasks/${id}/${file.name}`);
            await deleteObject(fileRef);
          } catch (error) {
            console.warn('Error deleting file:', error);
          }
        }
      }

      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // Enhanced Task Sharing Methods
  static async shareTask(taskId, userIds) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      // Verify user owns the task
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only share tasks you created');
      }

      // Filter out user IDs that are already shared
      const currentSharedWith = taskData.sharedWith || [];
      const newUserIds = userIds.filter(id => !currentSharedWith.includes(id));

      if (newUserIds.length > 0) {
        await updateDoc(taskRef, {
          sharedWith: arrayUnion(...newUserIds),
          updatedAt: new Date()
        });
      }

      return { success: true, message: `Task shared with ${newUserIds.length} new user(s)` };
    } catch (error) {
      console.error('Error sharing task:', error);
      throw error;
    }
  }

  static async unshareTask(taskId, userIds) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      // Verify user owns the task
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only unshare tasks you created');
      }

      // Remove users from sharedWith array
      await updateDoc(taskRef, {
        sharedWith: arrayRemove(...userIds),
        updatedAt: new Date()
      });

      return { success: true, message: `Task unshared from ${userIds.length} user(s)` };
    } catch (error) {
      console.error('Error unsharing task:', error);
      throw error;
    }
  }

  // Get shared users for a task
  static async getTaskSharedUsers(taskId) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskDoc = await getDoc(doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId));
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }

      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only view sharing info for tasks you created');
      }

      return taskData.sharedWith || [];
    } catch (error) {
      console.error('Error getting shared users:', error);
      throw error;
    }
  }

  // Bulk share task with multiple users
  static async bulkShareTask(taskId, userIds, shareType = 'read') {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      // Verify user owns the task
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.createdBy !== userId) {
        throw new Error('Permission denied: You can only share tasks you created');
      }

      // Add sharing metadata
      const sharingInfo = userIds.map(id => ({
        userId: id,
        shareType: shareType,
        sharedAt: new Date()
      }));

      await updateDoc(taskRef, {
        sharedWith: arrayUnion(...userIds),
        sharingInfo: arrayUnion(...sharingInfo),
        updatedAt: new Date()
      });

      return { success: true, message: `Task shared with ${userIds.length} user(s)` };
    } catch (error) {
      console.error('Error bulk sharing task:', error);
      throw error;
    }
  }

  // Media Upload
  static async uploadMedia(file, taskId, fileName = null) {
    AssessmentService.ensureAuthenticated();

    try {
      // Validate inputs
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (!taskId) {
        throw new Error('Task ID is required');
      }

      // Remove this permission check - let Storage rules handle it
      // const canModify = await this.canModifyTask(taskId);
      // if (!canModify) {
      //   throw new Error('Permission denied: You cannot upload files to this task');
      // }

      // Validate file size (50MB limit)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        throw new Error('File size exceeds 50MB limit');
      }

      // Validate file type
      const allowedTypes = [
        // Images
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
        // Videos
        'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/webm',
        // Audio
        'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a',
        // Documents
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'text/csv'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error('File type not allowed. Please upload images, videos, audio, documents, or text files.');
      }

      // Sanitize filename
      const actualFileName = fileName || file.name;
      const sanitizedFileName = this.sanitizeFileName(actualFileName);
      
      // Create storage reference
      const storageRef = ref(storage, `assessment-tasks/${taskId}/${sanitizedFileName}`);
      
      // Upload with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'uploadedBy': AssessmentService.getCurrentUserId(),
          'uploadedAt': new Date().toISOString(),
          'originalName': file.name
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        name: sanitizedFileName,
        originalName: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Provide more specific error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Permission denied: You are not authorized to upload files to this task');
      } else if (error.code === 'storage/invalid-format') {
        throw new Error('Invalid file format');
      } else if (error.code === 'storage/object-not-found') {
        throw new Error('Upload location not found');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded');
      } else {
        throw new Error(error.message || 'Failed to upload file');
      }
    }
  }

  // Keep this helper method for filename sanitization
  static sanitizeFileName(fileName) {
    // Remove potentially harmful characters and ensure reasonable length
    return fileName
      .replace(/[<>:"/\\|?*]/g, '_') // Replace harmful characters with underscore
      .replace(/\s+/g, '_') // Replace spaces with underscore
      .toLowerCase()
      .substring(0, 255); // Limit to 255 characters
  }

  static async deleteMedia(taskId, fileName) {
    AssessmentService.ensureAuthenticated();

    try {
      const fileRef = ref(storage, `assessment-tasks/${taskId}/${fileName}`);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw new Error('Failed to delete file');
    }
  }

  // Task Submissions
  static async createTaskSubmission(submission) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.TASK_SUBMISSIONS), {
        ...submission,
        createdBy: userId,
        createdAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw new Error('Failed to create submission');
    }
  }

  static async getTaskSubmissions(taskId = null) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      let q;
      if (taskId) {
        // Check if user can access this task
        const canAccess = await this.canAccessTask(taskId);
        if (!canAccess) {
          throw new Error('Permission denied: You cannot access submissions for this task');
        }

        q = query(
          collection(db, COLLECTIONS.TASK_SUBMISSIONS),
          where('taskId', '==', taskId),
          orderBy('date', 'desc')
        );
      } else {
        // Get all submissions for tasks user can access
        const accessibleTaskIds = await AssessmentService.getAccessibleTaskIds();
        if (accessibleTaskIds.length === 0) {
          return [];
        }

        q = query(
          collection(db, COLLECTIONS.TASK_SUBMISSIONS),
          where('taskId', 'in', accessibleTaskIds.slice(0, 10)), // Firestore 'in' limit
          orderBy('date', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      }));
    } catch (error) {
      console.error('Error fetching submissions:', error);
      throw new Error('Failed to fetch submissions');
    }
  }

  static async updateTaskSubmission(submissionId, submissionData) {
    try {
      const submissionRef = doc(db, COLLECTIONS.TASK_SUBMISSIONS, submissionId);
      await updateDoc(submissionRef, {
        ...submissionData,
        updatedAt: serverTimestamp()
      });
      return submissionId;
    } catch (error) {
      console.error('Error updating task submission:', error);
      throw error;
    }
  }

  // Learner Marks
  static async createLearnerMark(mark) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Verify user can access the task
      const canAccess = await this.canAccessTask(mark.taskId);
      if (!canAccess) {
        throw new Error('Permission denied: You cannot create marks for this task');
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.LEARNER_MARKS), {
        ...mark,
        createdBy: userId,
        createdAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating learner mark:', error);
      throw new Error('Failed to create learner mark');
    }
  }

  static async getLearnerMarks(taskId = null, submissionId = null) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      let q = query(collection(db, COLLECTIONS.LEARNER_MARKS));
      
      const conditions = [];

      if (taskId) {
        // Check if user can access this task
        const canAccess = await this.canAccessTask(taskId);
        if (!canAccess) {
          throw new Error('Permission denied: You cannot access marks for this task');
        }
        conditions.push(where('taskId', '==', taskId));
      } else {
        // Get marks for accessible tasks only
        const accessibleTaskIds = await AssessmentService.getAccessibleTaskIds();
        if (accessibleTaskIds.length === 0) {
          return [];
        }
        conditions.push(where('taskId', 'in', accessibleTaskIds.slice(0, 10))); // Firestore 'in' limit
      }

      if (submissionId) {
        conditions.push(where('submissionId', '==', submissionId));
      }

      // Apply all conditions
      q = query(q, ...conditions);

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      }));
    } catch (error) {
      console.error('Error fetching learner marks:', error);
      throw new Error('Failed to fetch learner marks');
    }
  }

  static async bulkCreateLearnerMarks(marks) {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      // Verify user can access all tasks
      const taskIds = [...new Set(marks.map(mark => mark.taskId))];
      for (const taskId of taskIds) {
        const canAccess = await this.canAccessTask(taskId);
        if (!canAccess) {
          throw new Error(`Permission denied: You cannot create marks for task ${taskId}`);
        }
      }

      const promises = marks.map(mark => 
        addDoc(collection(db, COLLECTIONS.LEARNER_MARKS), {
          ...mark,
          createdBy: userId,
          createdAt: new Date()
        })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error bulk creating learner marks:', error);
      throw new Error('Failed to create learner marks');
    }
  }

  // Helper method to get accessible task IDs
  static async getAccessibleTaskIds() {
    const userId = AssessmentService.ensureAuthenticated();

    try {
      const tasks = await this.getAssessmentTasks();
      return tasks.map(task => task.id);
    } catch (error) {
      console.error('Error getting accessible task IDs:', error);
      return [];
    }
  }

  // Check if user can access task
  static async canAccessTask(taskId) {
    const userId = this.getCurrentUserId();
    if (!userId) return false;

    try {
      const taskDoc = await getDoc(doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId));
      if (!taskDoc.exists()) return false;

      const taskData = taskDoc.data();
      return taskData.createdBy === userId || taskData.sharedWith?.includes(userId);
    } catch (error) {
      console.error('Error checking task access:', error);
      return false;
    }
  }

  // Get task owner info
  static async getTaskOwner(taskId) {
    try {
      const taskDoc = await getDoc(doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId));
      if (!taskDoc.exists()) throw new Error('Task not found');

      return taskDoc.data().createdBy;
    } catch (error) {
      console.error('Error getting task owner:', error);
      throw error;
    }
  }

  // Real-time subscription methods remain the same...
  static subscribeToTasks(callback) {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const q = query(
        collection(db, COLLECTIONS.ASSESSMENT_TASKS),
        or(
          where('createdBy', '==', userId),
          where('sharedWith', 'array-contains', userId)
        ),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const tasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
        }));
        callback(tasks);
      }, (error) => {
        console.error('Error in task subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up task subscription:', error);
      throw new Error('Failed to set up real-time updates');
    }
  }

  static subscribeToTask(taskId, callback) {
    AssessmentService.ensureAuthenticated();

    try {
      const taskRef = doc(db, COLLECTIONS.ASSESSMENT_TASKS, taskId);
      
      return onSnapshot(taskRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          callback({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        } else {
          callback(null);
        }
      }, (error) => {
        console.error('Error in task subscription:', error);
        callback(null);
      });
    } catch (error) {
      console.error('Error setting up task subscription:', error);
      throw new Error('Failed to set up real-time updates');
    }
  }

  static async deleteTaskSubmission(submissionId) {
  const userId = AssessmentService.ensureAuthenticated();

  try {
    const submissionRef = doc(db, COLLECTIONS.TASK_SUBMISSIONS, submissionId);
    
    // Verify user owns the submission before deleting
    const submissionDoc = await getDoc(submissionRef);
    if (!submissionDoc.exists()) {
      throw new Error('Submission not found');
    }
    
    const submissionData = submissionDoc.data();
    if (submissionData.createdBy !== userId) {
      throw new Error('Permission denied: You can only delete submissions you created');
    }

    await deleteDoc(submissionRef);
  } catch (error) {
    console.error('Error deleting submission:', error);
    throw error;
  }
}
static async updateLearnerMark(markId, updates) {
  const userId = this.ensureAuthenticated();

  try {
    const markRef = doc(db, COLLECTIONS.LEARNER_MARKS, markId);
    
    // Verify user owns the mark before updating
    const markDoc = await getDoc(markRef);
    if (!markDoc.exists()) {
      throw new Error('Mark not found');
    }
    
    const markData = markDoc.data();
    if (markData.createdBy !== userId) {
      throw new Error('Permission denied: You can only edit marks you created');
    }

    await updateDoc(markRef, {
      ...updates,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating mark:', error);
    throw error;
  }
}

static async deleteLearnerMark(markId) {
  const userId = this.ensureAuthenticated();

  try {
    const markRef = doc(db, COLLECTIONS.LEARNER_MARKS, markId);
    
    // Verify user owns the mark before deleting
    const markDoc = await getDoc(markRef);
    if (!markDoc.exists()) {
      throw new Error('Mark not found');
    }
    
    const markData = markDoc.data();
    if (markData.createdBy !== userId) {
      throw new Error('Permission denied: You can only delete marks you created');
    }

    await deleteDoc(markRef);
  } catch (error) {
    console.error('Error deleting mark:', error);
    throw error;
  }
}
static async createLearner(learnerData) {
    const userId = this.ensureAuthenticated();

    try {
      // Check if learner already exists
      const existingLearner = await this.findLearner(
        learnerData.learnerSurname, 
        learnerData.learnerName, 
        learnerData.grade, 
        learnerData.subject
      );

      if (existingLearner) {
        return existingLearner.id;
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.LEARNERS), {
        ...learnerData,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating learner:', error);
      throw new Error('Failed to create learner');
    }
  }

  static async findLearner(surname, name, grade, subject) {
    const userId = this.ensureAuthenticated();

    try {
      // Try finding with assessment field names first
      let q = query(
        collection(db, COLLECTIONS.LEARNERS),
        where('learnerSurname', '==', surname),
        where('learnerName', '==', name),
        where('grade', '==', grade),
        where('subject', '==', subject)
      );

      let querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return {
          id: querySnapshot.docs[0].id,
          ...querySnapshot.docs[0].data()
        };
      }

      // Also try finding in detailed learner collection with different field names
      q = query(
        collection(db, 'taskdata-learners'),
        where('surname', '==', surname),
        where('firstName', '==', name.split(' ')[0]) // Take first name part
      );

      querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        // Return with mapped field names
        return {
          id: querySnapshot.docs[0].id,
          learnerSurname: data.surname,
          learnerName: data.firstName,
          school: data.schoolName,
          grade: grade,
          subject: subject,
          ...data
        };
      }

      return null;
    } catch (error) {
      console.error('Error finding learner:', error);
      return null;
    }
  }

  static async getLearners(grade = null, subject = null) {
    const userId = this.ensureAuthenticated();

    try {
      let q = query(
        collection(db, COLLECTIONS.LEARNERS),
        where('createdBy', '==', userId),
        orderBy('learnerSurname', 'asc')
      );

      if (grade) {
        q = query(q, where('grade', '==', grade));
      }

      if (subject) {
        q = query(q, where('subject', '==', subject));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      }));
    } catch (error) {
      console.error('Error fetching learners:', error);
      throw new Error('Failed to fetch learners');
    }
  }

  // Itemised Marks Management
  static async createItemisedMarks(itemisedMarksData) {
    const userId = this.ensureAuthenticated();

    try {
      // Verify user can access the task
      const canAccess = await this.canAccessTask(itemisedMarksData.taskId);
      if (!canAccess) {
        throw new Error('Permission denied: You cannot create itemised marks for this task');
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.ITEMISED_MARKS), {
        ...itemisedMarksData,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating itemised marks:', error);
      throw new Error('Failed to create itemised marks');
    }
  }

  static async getItemisedMarks(learnerMarkId) {
    const userId = this.ensureAuthenticated();

    try {
      const q = query(
        collection(db, COLLECTIONS.ITEMISED_MARKS),
        where('learnerMarkId', '==', learnerMarkId),
        where('createdBy', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      }));
    } catch (error) {
      console.error('Error fetching itemised marks:', error);
      throw new Error('Failed to fetch itemised marks');
    }
  }

  static async updateItemisedMarks(itemisedMarkId, updates) {
    const userId = this.ensureAuthenticated();

    try {
      const itemisedMarkRef = doc(db, COLLECTIONS.ITEMISED_MARKS, itemisedMarkId);
      
      // Verify user owns the itemised mark before updating
      const itemisedMarkDoc = await getDoc(itemisedMarkRef);
      if (!itemisedMarkDoc.exists()) {
        throw new Error('Itemised mark not found');
      }
      
      const itemisedMarkData = itemisedMarkDoc.data();
      if (itemisedMarkData.createdBy !== userId) {
        throw new Error('Permission denied: You can only edit itemised marks you created');
      }

      await updateDoc(itemisedMarkRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating itemised marks:', error);
      throw error;
    }
  }

  static async deleteItemisedMarks(itemisedMarkId) {
    const userId = this.ensureAuthenticated();

    try {
      const itemisedMarkRef = doc(db, COLLECTIONS.ITEMISED_MARKS, itemisedMarkId);
      
      // Verify user owns the itemised mark before deleting
      const itemisedMarkDoc = await getDoc(itemisedMarkRef);
      if (!itemisedMarkDoc.exists()) {
        throw new Error('Itemised mark not found');
      }
      
      const itemisedMarkData = itemisedMarkDoc.data();
      if (itemisedMarkData.createdBy !== userId) {
        throw new Error('Permission denied: You can only delete itemised marks you created');
      }

      await deleteDoc(itemisedMarkRef);
    } catch (error) {
      console.error('Error deleting itemised marks:', error);
      throw error;
    }
  }

  // Enhanced learner mark creation with learner record
  static async createLearnerMarkWithLearner(markData, learnerData) {
    const userId = this.ensureAuthenticated();

    try {
      // Map field names consistently before creating learner
      const normalizedLearnerData = {
        learnerSurname: learnerData.learnerSurname || learnerData.surname || '',
        learnerName: learnerData.learnerName || learnerData.firstName || '',
        school: learnerData.school || learnerData.schoolName || '',
        grade: learnerData.grade || '',
        subject: learnerData.subject || ''
      };

      // Check if learner already exists with either field name format
      const existingLearner = await this.findLearner(
        normalizedLearnerData.learnerSurname,
        normalizedLearnerData.learnerName,
        normalizedLearnerData.grade,
        normalizedLearnerData.subject
      );

      let learnerId;
      
      if (existingLearner) {
        learnerId = existingLearner.id;
      } else {
        // Create new learner with consistent field names
        learnerId = await this.createLearner(normalizedLearnerData);
      }

      // Create learner mark with learner reference
      const enhancedMarkData = {
        ...markData,
        learnerId: learnerId,
        createdBy: userId,
        createdAt: new Date()
      };

      const markDocRef = await addDoc(collection(db, COLLECTIONS.LEARNER_MARKS), enhancedMarkData);
      return markDocRef.id;
    } catch (error) {
      console.error('Error creating learner mark with learner:', error);
      throw new Error('Failed to create learner mark');
    }
  }

  static async getSubjectMetadata(subject) {
    const userId = this.ensureAuthenticated();

    try {
      const q = query(
        collection(db, COLLECTIONS.ASSESSMENT_TASKS),
        where('createdBy', '==', userId),
        where('subject', '==', subject)
      );

      const querySnapshot = await getDocs(q);
      const allTags = new Set();
      const allConcepts = new Set();
      const allSkills = new Set();
      const allTopics = new Set();

      querySnapshot.docs.forEach(doc => {
        const taskData = doc.data();
        
        // Extract from topics
        if (taskData.topics) {
          taskData.topics.forEach(topic => {
            if (topic.trim()) allTopics.add(topic.trim());
          });
        }

        // Extract from questions
        if (taskData.questions) {
          taskData.questions.forEach(question => {
            // Question level
            if (question.tags) {
              question.tags.forEach(tag => {
                if (tag.trim()) allTags.add(tag.trim());
              });
            }
            if (question.concepts) {
              question.concepts.forEach(concept => {
                if (concept.trim()) allConcepts.add(concept.trim());
              });
            }
            if (question.skills) {
              question.skills.forEach(skill => {
                if (skill.trim()) allSkills.add(skill.trim());
              });
            }

            // Subquestion level
            if (question.subquestions) {
              question.subquestions.forEach(subq => {
                if (subq.tags) {
                  subq.tags.forEach(tag => {
                    if (tag.trim()) allTags.add(tag.trim());
                  });
                }
                if (subq.concepts) {
                  subq.concepts.forEach(concept => {
                    if (concept.trim()) allConcepts.add(concept.trim());
                  });
                }
                if (subq.skills) {
                  subq.skills.forEach(skill => {
                    if (skill.trim()) allSkills.add(skill.trim());
                  });
                }
              });
            }
          });
        }
      });

      return {
        tags: Array.from(allTags).sort(),
        concepts: Array.from(allConcepts).sort(),
        skills: Array.from(allSkills).sort(),
        topics: Array.from(allTopics).sort()
      };
    } catch (error) {
      console.error('Error fetching subject metadata:', error);
      return {
        tags: [],
        concepts: [],
        skills: [],
        topics: []
      };
    }
  }
}